import { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import currency from "currency.js";
import SearchIcon from "@mui/icons-material/Search";
import BDateTimePicker from "../../common/input/BDateTimePicker";
import { useLocation } from "react-router-dom";
import { getQueryParam, equalsIgnoreCase, getStartTime, getStoreFilterStreets, orderMatchesStoreFilter } from "../../common/util";
import { useNavigate } from "react-router";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link as RouterLink } from "react-router-dom";
import UserAvatar from "../../common/UserAvatar";
import ItemFilter from "../../planner/component/ItemFilter";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { UserContext } from "../../context/UserContext";

const columns = [
  {
    field: "fulfillmentTimestamp",
    headerName: "Timestamp",
    type: "number",
    renderCell: (params) => {
      const ts = params.row.fulfillmentTimestamp;
      return (
        <Box py={1.5}>
          <Typography variant="body2" display="block">
            {ts != null ? moment(ts).format("ll") : "—"}
          </Typography>
          {ts != null ? (
            <Typography variant="caption" color="textSecondary" display="block">
              {moment(ts).format("h:mm a")}
            </Typography>
          ) : null}
        </Box>
      );
    },
    width: 140,
  },
  {
    field: "tip",
    headerName: "Tip Amount",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 120,
  },

  {
    field: "orderUid",
    headerName: "Staff",
    renderCell: (params) => (
      <Box py={1} display="flex" alignItems="center">
        {params.value && <UserAvatar uid={params.value} />}
      </Box>
    ),
    width: 72,
    sortable: false,
  },
  {
    field: "fulfillmentType",
    headerName: "Fulfillment type",
    renderCell: (params) => (
      <Box py={2}>
        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
          {params.value || "—"}
        </Typography>
      </Box>
    ),
    width: 130,
  },

  {
    field: "id",
    headerName: "Order",
    renderCell: (params) => (
      <IconButton component={RouterLink} to={`/order/${params.value}`}>
        <VisibilityOutlinedIcon />
      </IconButton>
    ),
    width: 50,
  },
];

/**
 * Same basis as the grid (togo → order ts; else fulfillment date + start of time slot).
 * @returns {number | null} epoch ms, or null if not derivable
 */
function getOrderFulfillmentInstantMs(o) {
  if (equalsIgnoreCase("togo", o.fulfillment?.type)) {
    const sec = o.ts?._seconds ?? o.ts?.seconds;
    if (sec == null) return null;
    return sec * 1000;
  }
  const dateStr = o.fulfillment?.date;
  if (!dateStr) return null;
  const hour = getStartTime(o.fulfillment?.time ?? "");
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) {
    return moment(dateStr).startOf("day").valueOf();
  }
  return moment(dateStr).hour(hour).minute(0).second(0).millisecond(0).valueOf();
}

function orderWithinTimeWindow(o, fromDt, toDt) {
  if (!fromDt || !toDt) return true;
  const ms = getOrderFulfillmentInstantMs(o);
  if (ms == null) return true;
  const t = moment(ms);
  const fromM = moment(fromDt);
  const toM = moment(toDt);
  return t.isSameOrAfter(fromM) && t.isSameOrBefore(toM);
}

export default function TipsReport() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  let location = useLocation();

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");

  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState({});
  const [filteredResults, setFilteredResults] = useState([]);

  const [loading, setLoading] = useState(false);
  const apiRef = useRef(null);
  const storeLocations = user?.config?.storeLocation || [];
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  const maxDateTime = moment().endOf("day").toDate();
  const defaultReferenceDate = moment().hour(5).minute(0).second(0).millisecond(0).toDate();
  const toReferenceDate = fromDt ? moment(fromDt).toDate() : defaultReferenceDate;

  // Check params to identify search criteria
  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(location.search, "from"),
      getQueryParam(location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      qFrom = moment()
        .subtract(1, "days")
        .hour(5)
        .minute(0)
        .second(0)
        .format("YYYY-MM-DDTHH:mm:ss");
      qTo = moment().hour(5).minute(0).second(0).format("YYYY-MM-DDTHH:mm:ss");
    }
    search(qFrom, qTo);
  }, [location.search]);

  useEffect(() => {
    if (fromDt && toDt && moment(toDt).isBefore(moment(fromDt))) {
      setToDt("");
    }
  }, [fromDt, toDt]);

  useEffect(() => {
    if (!filter || typeof filter !== "object") {
      setFilteredResults(orders);
      return;
    }
    const arr = orders.filter((o) => {
      for (const [k, v] of Object.entries(filter)) {
        if (v?.value) {
          if (k === "fulfillment" && !equalsIgnoreCase(o.fulfillment?.type, v.value)) {
            return false;
          }
          if (k === "store") {
            if (!orderMatchesStoreFilter(o, v.value, storeLocations)) {
              return false;
            }
          }
        }
      }
      return true;
    });
    setFilteredResults(arr);
  }, [filter, orders]);

  const runDateSearch = (e) => {
    e.preventDefault();
    navigate(`/report/tips?from=${encodeURIComponent(fromDt)}&to=${encodeURIComponent(toDt)}`);
  };

  const search = (fromDate, toDate) => {
    setLoading(true);
    const fromApi = moment(fromDate).format("YYYY-MM-DD");
    const toApi = moment(toDate).format("YYYY-MM-DD");
    bkstApi.get(`/my/report/tips?fromDt=${fromApi}&toDt=${toApi}`).then((res) => {
      setFilter({});
      const raw = res.data.orders ?? [];
      const inWindow = raw.filter((o) => orderWithinTimeWindow(o, fromDate, toDate));
      setOrders(
        inWindow.map((o) => ({
          ...o,
          fulfillmentTimestamp: getOrderFulfillmentInstantMs(o),
        }))
      );

      setFromDt(fromDate);
      setToDt(toDate);

      setLoading(false);
    });
  };

  const exportToCSV = () => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv({
        fileName: `Tips_${moment(fromDt).format("YYYY-MM-DD")}_${moment(toDt).format("YYYY-MM-DD")}`,
        fields: ["fulfillmentTimestamp", "tip", "fulfillmentType"],
      });
    }
  };

  let tips = 0;
  for (const o of filteredResults) {
    tips += +o.payment.tip;
  }

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Box>
        <Container maxWidth="md">
          <Box mt={4}>
            <Typography variant="h1" gutterBottom>
              Tips Report
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              Shows tips accrued by order fulfillment timestamp.
            </Typography>
            <Box my={3}>
              <form onSubmit={runDateSearch}>
                <Box sx={{ display: "flex", gap: "10px" }}>
                  <BDateTimePicker
                    value={fromDt ? moment(fromDt).toDate() : null}
                    variant="filled"
                    label="From Date"
                    onChange={(d) => setFromDt(d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : "")}
                    required
                    hideIcon
                    referenceDate={defaultReferenceDate}
                    maxDateTime={maxDateTime}
                  />
                  <BDateTimePicker
                    value={toDt ? moment(toDt).toDate() : null}
                    variant="filled"
                    label="To Date"
                    onChange={(d) => setToDt(d ? moment(d).format("YYYY-MM-DDTHH:mm:ss") : "")}
                    required
                    hideIcon
                    referenceDate={toReferenceDate}
                    maxDateTime={maxDateTime}
                    minDateTime={fromDt ? moment(fromDt).toDate() : undefined}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ height: "56px" }}
                  >
                    <SearchIcon />
                  </Button>
                </Box>
              </form>
            </Box>
          </Box>
          {orders && orders.length > 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box>
                  <ItemFilter
                    config={[
                      {
                        name: "fulfillment",
                        options: orders.map((o) => o.fulfillment?.type),
                      },
                      showStoreFilter ? { name: "store", options: storeStreets } : "",
                    ]}
                    data={orders}
                    onChange={(o) => {
                      const next = typeof o === "object" && o !== null ? o : {};
                      setFilter({ ...filter, ...next });
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                {filteredResults && filteredResults.length > 0 && (
                  <Box
                    px={2}
                    py={2}
                    textAlign="center"
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      backgroundColor: "#FFF",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="overline" color="textSecondary">
                      TOTAL
                    </Typography>
                    <Typography variant="h4">{currency(tips).format()}</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}

          {filteredResults.length > 0 && (
            <Box mt={2}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <IconButton onClick={exportToCSV}>
                  <FileDownloadOutlinedIcon />
                </IconButton>
              </Box>
              <Box sx={{ width: "100%", display: "grid" }}>
                <DataGrid
                  apiRef={apiRef}
                  rows={filteredResults.map((o) => ({
                    id: o.id,
                    fulfillmentTimestamp: o.fulfillmentTimestamp,
                    orderUid: o.uid || "",
                    fulfillmentType: o.fulfillment?.type || "",
                    tip: o.payment.tip,
                  }))}
                  columns={columns}
                  autoHeight
                  getRowHeight={(params) => "auto"}
                  disableRowSelectionOnClick
                  initialState={{
                    sorting: {
                      sortModel: [{ field: "fulfillmentTimestamp", sort: "asc" }],
                    },
                  }}
                />
              </Box>
            </Box>
          )}
          {filteredResults.length === 0 && orders.length > 0 && (
            <Box my={2} px={2} pb={2} textAlign="center" sx={{ border: "1px solid #DDD" }}>
              <Typography variant="subtitle1">Nothing found</Typography>
              <Typography variant="body1" color="textSecondary">
                Please change your filters and try again.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
    );
  }
}
